// src/encounter/services/use-save-procedure.ts
// Rail-scoped save hook for the IPS Procedures section (PROD-1266). Wraps the curl-verified
// Procedure endpoints (POST /api/Patients/CreateProcedure, PUT /api/Patients/procedure/{id})
// and invalidates BOTH the ENCOUNTER summary query (['patient-summary', patientId] — the rail
// render source) AND the full chart list (['patient-procedures', patientId] — the source the
// rail looks edit/delete records up from). Mirrors use-save-condition / use-save-allergy:
// transport + cache only; body assembly + wire-shape rules live in lib/to-procedure-request.ts.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type { ProcedureRequest } from '@/encounter/lib/to-procedure-request';

export function useSaveProcedure(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<string, Error, ProcedureRequest>({
    mutationFn: (req: ProcedureRequest) => {
      if (req.kind === 'create') {
        return post<string>('/api/Patients/CreateProcedure', req.body);
      }
      // update path covers EDIT and soft-DELETE.
      return put<string>(`/api/Patients/procedure/${req.procedureId}`, req.body);
    },
    onSuccess: () => {
      // Invalidate the summary (rail render source) AND the full chart list the rail sources
      // edit/delete from (usePatientProcedures, ['patient-procedures', patientId]). Without the
      // second key a freshly-added/edited row's full record stays stale and its affordance can't
      // complete the round-trip (mirrors use-save-condition).
      qc.invalidateQueries({ queryKey: ['patient-summary', patientId] });
      qc.invalidateQueries({ queryKey: ['patient-procedures', patientId] });
    },
  });
}
